// Katalog czesci w interfejsie.
//
// Lista budowana z CATALOG i pogrupowana po kategorii - przy dwudziestu kilku
// klockach plaska lista przestaje byc czytelna. HTML nie duplikuje ani katalogu,
// ani jego grupowania.
//
// Kazdy klocek ma MINIATURKE rysowana tym samym kodem co plotno (render/partThumb),
// zeby gracz widzial, co dokladnie buduje, bez czytania opisow.

import type { PartCategory, PartDefinition, RobotBlueprint } from '../domain/types';
import { partCost } from '../domain/costs';
import { CATEGORY_LABELS, remainingFor, type PartLimits } from '../domain/limits';
import { drawPartThumbnail } from '../render/partThumb';

/** Bok miniaturki klocka w CSS pikselach (kwadrat). */
const PREVIEW_PX = 44;

/** Kolejnosc grup w palecie: od konstrukcji do zasilania. */
const GROUP_ORDER: readonly PartCategory[] = [
  'frame',
  'mount',
  'wheel',
  'drive',
  'gear',
  'battery',
];

export class Palette {
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private readonly definitions = new Map<string, PartDefinition>();
  private selectedTypeId: string | null = null;
  /** Limity klockow biezacej trasy (undefined = bez ograniczen). */
  private limits: PartLimits | undefined;

  constructor(
    container: HTMLElement,
    catalog: readonly PartDefinition[],
    onPick: (typeId: string) => void,
  ) {
    container.replaceChildren(
      ...GROUP_ORDER.map((category) => this.buildGroup(category, catalog, onPick)).filter(
        (group): group is HTMLDivElement => group !== null,
      ),
    );
  }

  private buildGroup(
    category: PartCategory,
    catalog: readonly PartDefinition[],
    onPick: (typeId: string) => void,
  ): HTMLDivElement | null {
    const parts = catalog.filter((definition) => definition.category === category);
    if (parts.length === 0) return null;

    const group = document.createElement('div');
    group.className = 'palette-group';

    const title = document.createElement('span');
    title.className = 'palette-group-title';
    title.textContent = CATEGORY_LABELS[category];
    group.append(title);

    const row = document.createElement('div');
    row.className = 'palette-row';
    for (const definition of parts) {
      const button = this.buildButton(definition, onPick);
      this.buttons.set(definition.typeId, button);
      this.definitions.set(definition.typeId, definition);
      row.append(button);
    }
    group.append(row);
    return group;
  }

  private buildButton(
    definition: PartDefinition,
    onPick: (typeId: string) => void,
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'part';
    button.dataset.typeId = definition.typeId;
    button.title = `${definition.label} - ${definition.massKg.toFixed(2)} kg - koszt ${partCost(definition)}`;

    const name = document.createElement('span');
    name.className = 'part-name';
    name.textContent = definition.label;

    const meta = document.createElement('span');
    meta.className = 'part-meta';
    meta.textContent = `${definition.footprint.w}x${definition.footprint.h} - ${definition.massKg.toFixed(2)} kg`;

    const body = document.createElement('span');
    body.className = 'part-body';
    body.append(name, meta);

    // Cena klocka obok nazwy - od razu widac, na co wystarczy budzet.
    const price = document.createElement('span');
    price.className = 'part-price';
    price.textContent = String(partCost(definition));

    // Licznik limitu trasy (pusty, gdy klocek bez limitu). Wypelnia updateCounts.
    const limit = document.createElement('span');
    limit.className = 'part-limit';
    limit.setAttribute('aria-hidden', 'true');

    button.append(this.buildPreview(definition), body, limit, price);
    button.addEventListener('click', () => onPick(definition.typeId));
    return button;
  }

  /**
   * Miniaturka klocka: maly canvas z tym samym rysunkiem co na plotnie. Skalujemy
   * przez devicePixelRatio, zeby na ekranach HiDPI byla ostra.
   */
  private buildPreview(definition: PartDefinition): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.className = 'part-preview';
    canvas.setAttribute('aria-hidden', 'true');

    const ratio = Math.max(1, Math.min(3, globalThis.devicePixelRatio || 1));
    const side = Math.round(PREVIEW_PX * ratio);
    canvas.width = side;
    canvas.height = side;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.save();
      ctx.scale(ratio, ratio);
      drawPartThumbnail(ctx, definition, PREVIEW_PX);
      ctx.restore();
    }
    return canvas;
  }

  /**
   * Ustawia limity klockow biezacej trasy (undefined = bez ograniczen). Sama
   * zmiana limitu nie odswieza przyciskow - robi to `updateCounts` po zmianie
   * konstrukcji, zeby liczniki i blokady byly zawsze aktualne.
   */
  setLimits(limits: PartLimits | undefined): void {
    this.limits = limits;
  }

  /**
   * Odswieza licznik limitu ("ile jeszcze wolno") i wylacza klocki, ktorych
   * limit trasy jest wyczerpany. Wola to controller po kazdej zmianie konstrukcji.
   */
  updateCounts(blueprint: RobotBlueprint): void {
    for (const [typeId, button] of this.buttons) {
      const definition = this.definitions.get(typeId);
      if (!definition) continue;

      const remaining = remainingFor(definition, blueprint, this.limits);
      const badge = button.querySelector<HTMLElement>('.part-limit');
      if (badge) badge.textContent = remaining === undefined ? '' : String(remaining);

      const exhausted = remaining !== undefined && remaining <= 0;
      button.disabled = exhausted;

      const base = `${definition.label} - ${definition.massKg.toFixed(2)} kg - koszt ${partCost(definition)}`;
      button.title =
        remaining === undefined ? base : `${base} - limit trasy: zostalo ${remaining}`;
    }
  }

  get selected(): string | null {
    return this.selectedTypeId;
  }

  setSelected(typeId: string | null): void {
    this.selectedTypeId = typeId;
    for (const [id, button] of this.buttons) {
      button.classList.toggle('is-selected', id === typeId);
      button.setAttribute('aria-pressed', String(id === typeId));
    }
  }
}